import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Alert, Badge, Button, Card, CardBody, CardHeader, EmptyState, Skeleton } from '../../components/ui'
import { WrenchIcon } from '../../components/icons'

interface EquipmentSummary {
  equipment_id: string
  equipment_name?: string
  manufacturer?: string
  model?: string
  equipment_class?: string
  description?: string
  assembly_count?: number
  component_count?: number
  critical_problems?: number
}

export default function Equipment() {
  const [classFilter, setClassFilter] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'equipment'],
    queryFn: () => api.get<{ equipment: EquipmentSummary[] }>('/equipment'),
  })

  const all = data?.equipment ?? []
  const classes = [...new Set(all.map((e) => e.equipment_class || 'General'))].sort()
  const filtered = classFilter ? all.filter((e) => (e.equipment_class || 'General') === classFilter) : all

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Equipment reference</h1>
          <p className="page-head__sub">Mining equipment catalogue — specs, assemblies and known issues.</p>
        </div>
      </div>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : (
        <>
          {classes.length > 0 && (
            <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
              <Button size="sm" variant={classFilter === '' ? 'primary' : 'secondary'} onClick={() => setClassFilter('')}>
                All ({all.length})
              </Button>
              {classes.map((c) => (
                <Button key={c} size="sm" variant={classFilter === c ? 'primary' : 'secondary'}
                        onClick={() => setClassFilter(c)}>
                  {c} ({all.filter((e) => (e.equipment_class || 'General') === c).length})
                </Button>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="grid grid--2"><Skeleton h={100} /><Skeleton h={100} /></div>
          ) : filtered.length === 0 ? (
            <Card><CardBody><EmptyState icon={<WrenchIcon size={22} />} title="No equipment on file" body="" /></CardBody></Card>
          ) : (
            <div className="grid grid--2">
              {filtered.map((e) => (
                <Card key={e.equipment_id}>
                  <CardHeader
                    title={e.equipment_name}
                    sub={[e.manufacturer, e.model].filter(Boolean).join(' · ') || undefined}
                  />
                  <CardBody>
                    <p style={{ margin: '0 0 var(--sp-3)' }}>{e.description || 'No description.'}</p>
                    <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                      <Badge>{e.assembly_count ?? 0} assemblies</Badge>
                      <Badge>{e.component_count ?? 0} components</Badge>
                      {(e.critical_problems ?? 0) > 0 && (
                        <Badge tone="danger">{e.critical_problems} critical issue{e.critical_problems === 1 ? '' : 's'}</Badge>
                      )}
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </>
  )
}
